import StandardHead from "@components/standardHead";
import StripBanner from "@components/stripBanner";
import { addEcommerceEvent, getProductEcommerceItems } from "@lib/productEcommerceItem";
import { DataLayer } from "@lib/utils/dataLayer";
import { getProductOpenGraphImage } from "@lib/utils/getProductOpenGraphImage";
import { getStripBannerItems } from "@lib/utils/stripBannerHelper";
import { useAuthentication } from "@providers/AuthenticationProvider";
import { ProductDetailProvider } from "@providers/ProductDetailProvider";
import {
  EngineResponse,
  ProductMain,
  SanityProductPreset,
  SanityProductPresetPage,
} from "@sourceful/shared-types";
import React, { useEffect } from "react";
import ProductPageContent from "./ProductPageContent";
import ProductDetailPageStickyBar from "./productDetailStickyBar";
import ProductOverview from "./productOverview/ProductOverview";

export interface ProductDetailPageProps {
  product: ProductMain;
  presets: SanityProductPreset[];
  presetPageOverride?: SanityProductPresetPage;
  presetOverrideEngineMetadata?: EngineResponse;
}

// We need to merge our preset override into the product if we have one
// The fallback is the product so only do this if we have a useable value
function createProductPresetViewModel(
  presetPage: SanityProductPresetPage,
  product: ProductMain
): ProductMain {
  const { productPreset: preset } = presetPage;
  const mergedProduct = {
    ...product,
    name: presetPage.name || preset.name,
  };

  if (presetPage.description) {
    mergedProduct.description = presetPage.description;
  }

  if (presetPage.pageMeta) {
    mergedProduct.pageMeta = presetPage.pageMeta;
  }

  // preset images take precedence over product images
  if (preset.images && preset.images.length > 0) {
    mergedProduct.images = preset.images;
  }

  // preset page images take precence over preset images
  if (presetPage.images && presetPage.images.length > 0) {
    mergedProduct.images = presetPage.images;
  }

  if (presetPage.tags && presetPage.tags.length > 0) {
    mergedProduct.tags = presetPage.tags;
  }

  if (presetPage.fromPrice) {
    mergedProduct.fromPrice = presetPage.fromPrice;
  }

  if (presetPage.productDetailPageContent && presetPage.productDetailPageContent.length > 0) {
    mergedProduct.productDetailPageContent = presetPage.productDetailPageContent;
  }

  if (presetPage.stripBannerItems && presetPage.stripBannerItems.length > 0) {
    mergedProduct.stripBannerItems = presetPage.stripBannerItems;
  }

  return mergedProduct;
}

const ProductDetailPage: React.FC<ProductDetailPageProps> = ({
  product,
  presets,
  presetPageOverride: presetOverride,
  presetOverrideEngineMetadata,
}) => {
  const { isLoading } = useAuthentication();
  const displayConfigureButton = isLoading === false;

  const viewModel = presetOverride
    ? createProductPresetViewModel(presetOverride, product)
    : product;

  // register page view with GA - this custom event is needed to pass the config version to ga
  useEffect(() => {
    DataLayer.pushEvent(`product-page:${product.baseProductId}:v2`);
    DataLayer.pushEvent("product-page", {
      configVersion: "v2",
      baseProductId: product.baseProductId,
    });
  }, [product.baseProductId]);

  useEffect(() => {
    addEcommerceEvent({
      event: "view_item",
      ecommerce: {
        items: getProductEcommerceItems([
          {
            ...product,
            index: 0,
          },
        ]),
      },
    });
  }, [product]);

  const pageMeta = viewModel?.pageMeta;
  const ogImageUrl = getProductOpenGraphImage(viewModel, pageMeta);

  return (
    <>
      <StandardHead
        title={pageMeta?.metaTitle || viewModel.name}
        metadescription={pageMeta?.metaDescription}
        openGraph={{
          image: ogImageUrl,
        }}
      />
      <main>
        <ProductDetailProvider
          product={product}
          presets={presets}
          presetOverride={presetOverride?.productPreset}
          presetOverrideEngineMetadata={presetOverrideEngineMetadata}
        >
          {viewModel.stripBannerItems && viewModel.stripBannerItems.length > 0 && (
            <StripBanner mode="Paginated" items={getStripBannerItems(viewModel.stripBannerItems)} />
          )}

          <ProductOverview displayConfigureButton={displayConfigureButton} product={viewModel} />

          <ProductDetailPageStickyBar
            product={viewModel}
            renderConfigureButton={displayConfigureButton}
          />
          <ProductPageContent product={viewModel} />
        </ProductDetailProvider>
      </main>
    </>
  );
};

export default ProductDetailPage;
